// src/components/Sidebar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  FileTextIcon, StoreIcon, PackageIcon,
  Settings2Icon, HelpCircleIcon, UsersIcon, TruckIcon,
  MenuIcon, XIcon, ChevronRightIcon, ChevronDownIcon,
  PanelLeftOpen, PanelLeftClose, BarChart, Upload,
  User, ChevronDown
} from 'lucide-react';

import LogoImage from '../GastroFlow Logo Schwarz.png';

// Quadratisches Logo für die Rail
const COLLAPSED_LOGO_URL = 'https://i.ibb.co/4Zgzc58N/gastroflow-g-logo.png';

/* ----------------------------- Menüdefinition ----------------------------- */
const menuItems = [
  {
    id: 'analytics',
    title: "Analytics",
    icon: BarChart,
    items: [
      { id: 'analytics-dashboard', label: 'Dashboard', path: '/analytics', permission: 'read:analytics' },
      { id: 'analytics-drivers', label: 'Fahrer Performance', path: '/analytics/drivers', permission: 'read:analytics' },
      { id: 'analytics-organizations', label: 'Organisationen', path: '/analytics/organizations', permission: 'read:analytics' },
      { id: 'analytics-locations', label: 'Standorte', path: '/analytics/locations', permission: 'read:analytics' },
      { id: 'analytics-documents', label: 'Dokumente', path: '/analytics/documents', permission: 'read:analytics' },
      { id: 'analytics-onboarding', label: 'Onboarding', path: '/analytics/onboarding', permission: 'read:analytics' },
    ]
  },
  {
    id: 'data',
    title: "Data",
    icon: Upload,
    items: [
      { id: 'uploads', label: 'Uploads', path: '/uploads', permission: 'read:analytics' },
      { id: 'reports', label: 'Berichte', path: '/reports', permission: 'read:analytics' },
    ]
  },
  {
    id: 'orders',
    title: "Bestellungen & Finanzen",
    icon: FileTextIcon,
    items: [
      { id: 'orders', label: 'Bestellungen', path: '/page1', permission: 'manage:orders' },
      { id: 'billing', label: 'Abrechnungen', path: '/BillingManagement', permission: 'manage:billing' },
    ]
  },
  {
    id: 'partners',
    title: "Partner & Standorte",
    icon: StoreIcon,
    items: [
      { id: 'servicePartner', label: 'Organisationen', path: '/fleetpartner', permission: 'manage:partners' },
      { id: 'locations', label: 'Standorte', path: '/locations', permission: 'manage:locations' },
    ]
  },
  {
    id: 'inventory',
    title: "Inventar",
    icon: PackageIcon,
    items: [
      { id: 'assets', label: 'Assets', path: '/assets', permission: 'manage:assets' },
      { id: 'recipes', label: 'Rezepte', path: '/recepies', permission: 'manage:recipes' },
    ]
  },
  {
    id: 'delivery',
    title: "Lieferungen",
    icon: TruckIcon,
    items: [
      { id: 'deliveries', label: 'Lieferungen', path: '/deliveries', permission: 'manage:deliveries' },
      { id: 'routes', label: 'Routen', path: '/routes', permission: 'manage:routes' },
    ]
  },
  {
    id: 'staff',
    title: "Personal",
    icon: UsersIcon,
    items: [
      { id: 'applicants', label: 'Bewerbungen', path: '/applicants', permission: 'manage:applicants' },
      { id: 'drivers', label: 'Fahrer', path: '/drivers', permission: 'manage:applicants' },
    ]
  },
  {
    id: 'communication',
    title: "Dokumente & Kommunikation",
    icon: FileTextIcon,
    items: [
      { id: 'documents', label: 'Dokumente', path: '/documents', permission: 'read:documents' },
      { id: 'calendar', label: 'Kalender', path: '/calendar', permission: 'read:calendar' },
      { id: 'email', label: 'E-Mail', path: '/email', permission: 'manage:communication' },
      { id: 'chat', label: 'Chat', path: '/chat', permission: 'manage:communication' },
    ]
  },
];

/* ------------------------ Profil-Menü (ohne Icons/Avatar-Header) ---------- */
const profileMenuSections = [
  {
    title: 'Profil & Account',
    items: [
      { name: 'Profil bearbeiten', path: '/profile', permission: 'edit:profile' },
      { name: 'Account Einstellungen', path: '/account', permission: 'manage:account' },
      { name: 'Sicherheit', path: '/security', permission: 'manage:security' }
    ]
  },
  {
    title: 'Support & Hilfe',
    items: [
      { name: 'Dokumentation', path: '/docs', permission: 'read:docs' },
      { name: 'Support kontaktieren', path: '/support', permission: 'contact:support' },
      { name: 'Hilfe & FAQs', path: '/help', permission: 'read:help' }
    ]
  }
];

/* -------------------------------- Helpers -------------------------------- */
const LS_IS_OPEN_KEY = 'sidebar:isOpen';
const LS_COLLAPSED_KEY = 'sidebar:collapsed';

const getInitialIsMobile = () =>
  typeof window !== 'undefined' ? window.innerWidth <= 768 : false;

const getInitialIsOpen = () => {
  if (typeof window === 'undefined') return true;
  const saved = localStorage.getItem(LS_IS_OPEN_KEY);
  if (saved !== null) return JSON.parse(saved);
  return !getInitialIsMobile(); // Desktop offen, Mobile zu
};

const getInitialCollapsed = () => {
  if (typeof window === 'undefined') return new Set();
  const saved = localStorage.getItem(LS_COLLAPSED_KEY);
  try {
    return new Set(saved ? JSON.parse(saved) : []);
  } catch {
    return new Set();
  }
};

const debounce = (fn, ms = 150) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
};

// Erste erlaubte Seite einer Sektion
const firstPermittedPath = (section, hasPermission) => {
  const firstItem = section.items.find(it => hasPermission(it.permission));
  return firstItem?.path || null;
};

/* ---------- Globale CSS-Helfer: Scrollbar ausblenden + Thread-Linien ------ */
const INJECTED_CSS = `
  /* Scrollbar verstecken */
  .no-scrollbar::-webkit-scrollbar { display: none; }
  .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

  /* Nur vertikale Thread-Linie links der Unterseitenliste */
  .thread-wrap { position: relative; }
  .thread-wrap::before {
    content: "";
    position: absolute;
    left: 12px;        /* Abstand zur linken Kante */
    top: 4px;          /* leicht eingerückt oben */
    bottom: 4px;       /* leicht eingerückt unten */
    width: 2px;
    background: #e5e7eb; /* Tailwind gray-200 */
    border-radius: 1px;
  }

  /* Alte horizontale Äste ENTFERNEN:
     .thread-item::before { ... }
     .thread-item[data-active="true"]::before { ... }
  */
`;


/* ----------- Reusable Scroll-Container mit Top/Bottom-Fade ---------------- */
const ScrollFade = ({ children, className = '' }) => {
  const ref = useRef(null);
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(true);

  const update = () => {
    const el = ref.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    setAtTop(scrollTop <= 0);
    setAtBottom(scrollTop + clientHeight >= scrollHeight - 1);
  };

  useEffect(() => {
    update();
    const el = ref.current;
    if (!el) return;
    const onScroll = () => update();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div className={`relative min-h-0 ${className}`}>
      <div ref={ref} className="h-full overflow-y-auto no-scrollbar">
        {children}
      </div>

      {/* Top-Fade */}
      <div
        className={`pointer-events-none absolute inset-x-0 top-0 h-4 transition-opacity ${atTop ? 'opacity-0' : 'opacity-100'}`}
        style={{ background: 'linear-gradient(to bottom, rgba(255,255,255,1), rgba(255,255,255,0))' }}
      />
      {/* Bottom-Fade */}
      <div
        className={`pointer-events-none absolute inset-x-0 bottom-0 h-4 transition-opacity ${atBottom ? 'opacity-0' : 'opacity-100'}`}
        style={{ background: 'linear-gradient(to top, rgba(255,255,255,1), rgba(255,255,255,0))' }}
      />
    </div>
  );
};

/* -------------------------------- Component ------------------------------- */
const Sidebar = ({ onResize }) => {
  const [isMobile, setIsMobile] = useState(getInitialIsMobile);
  const [isOpen, setIsOpen] = useState(getInitialIsOpen);
  const [userPermissions, setUserPermissions] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState(getInitialCollapsed);
  const [profileOpen, setProfileOpen] = useState(false);

  const profileRef = useRef(null);

  const railWidth = 72;
  const openWidth = 260;
  const sidebarWidth = isMobile ? (isOpen ? '100%' : '0') : (isOpen ? openWidth : railWidth);

  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, isAuthenticated, getAccessTokenSilently, user, logout } = useAuth0();

  /* --------- Permissions aus Token -------- */
  useEffect(() => {
    const checkToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        if (token) {
          const parts = token.split('.');
          if (parts.length === 3) {
            const decodedToken = JSON.parse(atob(parts[1]));
            const permissions =
              decodedToken.permissions ||
              decodedToken?.['https://api.gastroflow.com/auth/permissions'] ||
              [];
            setUserPermissions(permissions);
          }
        }
      } catch (error) {
        console.error('Error checking token:', error);
      }
    };
    if (isAuthenticated) checkToken();
  }, [isAuthenticated, getAccessTokenSilently]);

  /* -------------------------- Resize ------------------------- */
  useEffect(() => {
    const onResizeDebounced = debounce(() => {
      const mobile = getInitialIsMobile();
      setIsMobile(mobile);
      if (typeof onResize === 'function' && !mobile) {
        onResize(isOpen ? openWidth : railWidth);
      }
    }, 150);

    window.addEventListener('resize', onResizeDebounced);
    if (typeof onResize === 'function' && !isMobile) {
      onResize(isOpen ? openWidth : railWidth);
    }
    return () => window.removeEventListener('resize', onResizeDebounced);
  }, [isOpen, isMobile, onResize]);

  /* ----------------------------- Persistenz ----------------------------- */
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(LS_IS_OPEN_KEY, JSON.stringify(isOpen));
    }
  }, [isOpen]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(LS_COLLAPSED_KEY, JSON.stringify([...collapsedSections]));
    }
  }, [collapsedSections]);

  /* -------------------------- Profile: Outside/Escape ---------------------- */
  useEffect(() => {
    const onClick = (e) => {
      if (!profileRef.current) return;
      if (!profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setProfileOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  /* ------------------------------- Helpers UI ----------------------------- */
  const hasPermission = (requiredPermission) => {
    if (!requiredPermission) return true;
    return userPermissions.includes(requiredPermission);
  };

  const blurActive = () => {
    if (document && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const handleItemClick = (path) => {
    try {
      navigate(path);
      blurActive();
      if (isMobile) setIsOpen(false);
    } catch (e) {
      console.warn('Navigation error:', e);
      // Fallback: window.location verwenden
      window.location.href = path;
    }
  };

  const toggleSidebar = () => {
    setIsOpen(prev => !prev);
    blurActive();
  };

  const handleSectionClick = (section) => {
    blurActive();

    if (!isOpen && !isMobile) {
      const path = firstPermittedPath(section, hasPermission);
      if (path) {
        try {
          navigate(path);
        } catch (e) {
          console.warn('Navigation error:', e);
          window.location.href = path;
        }
      }
      return;
    }
    setCollapsedSections(prev => {
      const next = new Set(prev);
      next.has(section.id) ? next.delete(section.id) : next.add(section.id);
      return next;
    });
  };

  const handleLogoClick = () => {
    try {
      navigate('/');
      blurActive();
      if (isMobile) setIsOpen(false);
    } catch (e) {
      console.warn('Navigation error:', e);
      // Fallback: window.location verwenden
      window.location.href = '/';
    }
  };

  /* -------------------------- Lade-/Auth-Zustände ------------------------- */
  if (isLoading) {
    return (
      <div className="fixed top-0 left-0 h-16 w-full flex items-center justify-center bg-white z-50 border-b border-gray-200 md:h-screen md:w-16 md:border-r md:border-b-0">
        <div className="text-sm text-gray-600">Lädt...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="fixed top-0 left-0 h-16 w-full flex items-center justify-center bg-white z-50 border-b border-gray-200 md:h-screen md:w-16 md:border-r md:border-b-0">
        <div className="text-sm text-gray-600">Nicht eingeloggt</div>
      </div>
    );
  }

  /* --------------------------------- Styles injizieren -------------------- */
  const styleTag = <style>{INJECTED_CSS}</style>;

  /* ------------------------------- Mobile Topbar --------------------------- */
  if (isMobile && !isOpen) {
    return (
      <>
        {styleTag}
        <div className="fixed top-0 left-0 w-full h-16 bg-white flex items-center justify-between px-4 z-50 border-b border-gray-200">
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleLogoClick}
            className="focus:outline-none focus-visible:outline-none"
            aria-label="Zum Dashboard"
            title="Dashboard"
          >
            <img src={LogoImage} alt="Logo" className="h-5 cursor-pointer" />
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setIsOpen(true)}
            className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus-visible:outline-none"
            aria-label="Menü öffnen"
          >
            <MenuIcon size={20} />
          </button>
        </div>
        <div className="h-16" />
      </>
    );
  }

  /* ------------------------------- Mobile Drawer -------------------------- */
  if (isMobile) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex flex-col">
        {styleTag}
        <div className="flex justify-between items-center py-4 px-4 border-b border-gray-200">
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleLogoClick}
            className="focus:outline-none focus-visible:outline-none"
            aria-label="Zum Dashboard"
            title="Dashboard"
          >
            <img src={LogoImage} alt="Logo" className="h-5 cursor-pointer" />
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setIsOpen(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus-visible:outline-none"
            aria-label="Menü schließen"
          >
            <XIcon size={20} />
          </button>
        </div>

        {/* Scrollbarer Bereich mit Fade (mobil) */}
        <ScrollFade className="flex-grow">
          <nav className="p-4">
            {menuItems.map((section) => {
              const hasAccessibleItems = section.items.some(item => hasPermission(item.permission));
              const isCollapsed = collapsedSections.has(section.id);
              const isSectionActive = section.items.some(item => location.pathname === item.path);
              if (!hasAccessibleItems) return null;

              return (
                <div key={section.id} className="mb-4">
                  <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSectionClick(section)}
                    className={`flex items-center w-full px-3 py-2 text-sm rounded-md transition-colors justify-between hover:bg-gray-50 ${
                      isSectionActive ? 'font-semibold text-black' : 'text-gray-700 hover:text-gray-900'
                    } focus:outline-none focus-visible:outline-none`}
                  >
                    <div className="flex items-center">
                      <section.icon size={18} className="mr-3" />
                      <span>{section.title}</span>
                    </div>
                    {isCollapsed ? <ChevronRightIcon size={16} /> : <ChevronDownIcon size={16} />}
                  </button>

                  <AnimatePresence initial={false}>
                    {!isCollapsed && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        {/* THREAD-LINIEN */}
                        <ul className="mt-2 space-y-1 thread-wrap">
                          {section.items.map((item) =>
                            hasPermission(item.permission) ? (
                              <li
                                key={item.id}
                                className="thread-item pl-7"
                                data-active={location.pathname === item.path}
                              >
                                <button
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => handleItemClick(item.path)}
                                  className={`flex items-center w-full px-3 py-2 text-sm rounded-md transition-colors ${
                                    location.pathname === item.path
                                      ? 'text-black bg-gray-100 font-medium'
                                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                  } focus:outline-none focus-visible:outline-none`}
                                >
                                  {item.label}
                                </button>
                              </li>
                            ) : null
                          )}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </nav>
        </ScrollFade>

        {/* Footer mit Profilblock und Buttons */}
        <div className="p-4 border-t border-gray-200 relative" ref={profileRef}>
          {/* Profil-Trigger */}
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setProfileOpen(prev => !prev)}
            className="w-full flex items-center gap-3 p-2 rounded-md bg-gray-50 active:bg-gray-100 transition-colors focus:outline-none focus-visible:outline-none"
            aria-expanded={profileOpen}
            aria-haspopup="true"
            title={user?.name || 'Profil'}
          >
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user?.name || 'User'}
                className="w-9 h-9 rounded-full ring-2 ring-white object-cover"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = 'https://ui-avatars.com/api/?name=User&size=200';
                }}
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
                <User className="w-4 h-4 text-gray-600" />
              </div>
            )}
            <div className="flex-1 text-left">
              <div className="text-sm font-medium text-gray-900 truncate">{user?.name || 'Benutzer'}</div>
              <div className="text-xs text-gray-500 truncate">{user?.email || ''}</div>
            </div>
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </button>

          {/* Menü nach rechts — unten ausgerichtet */}
          <AnimatePresence>
            {profileOpen && (
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 6, scale: 0.98 }}
                transition={{ type: 'tween', duration: 0.16 }}
                className="absolute left-full bottom-3 ml-2 w-[260px] z-50"
              >
                <div className="bg-white rounded-lg shadow-lg border border-gray-200 flex flex-col max-h[70vh]">
                  {/* Scrollbarer Mittelteil – ohne sichtbare Scrollbar */}
                  <div className="flex-1 overflow-y-auto no-scrollbar py-1">
                    {profileMenuSections.map((section, idx) => {
                      const hasAccess = section.items.some(item => hasPermission(item.permission));
                      if (!hasAccess) return null;
                      return (
                        <div key={section.title} className="px-2">
                          <div className="px-2 py-1.5">
                            <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                              {section.title}
                            </h3>
                          </div>
                          {section.items.map((item) =>
                            hasPermission(item.permission) ? (
                              <button
                                key={item.name}
                                onClick={() => {
                                  try {
                                    navigate(item.path);
                                    setProfileOpen(false);
                                  } catch (e) {
                                    console.warn('Navigation error:', e);
                                    window.location.href = item.path;
                                  }
                                }}
                                className="w-full text-left px-3 py-1.5 text-[13px] text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                              >
                                {item.name}
                              </button>
                            ) : null
                          )}
                          {idx < profileMenuSections.length - 1 && hasAccess && (
                            <div className="my-2 border-t border-gray-100" />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Fixer Footer (Abmelden) */}
                  <div className="px-2 py-2 border-t border-gray-100 bg-white">
                    <button
                      onClick={() => logout({ returnTo: window.location.origin })}
                      className="w-full text-left px-3 py-1.5 text-[13px] rounded-md text-red-600 hover:bg-red-50 transition-colors"
                    >
                      Abmelden
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Buttons unten */}
          <div className="mt-4 flex justify-around">
            {hasPermission('manage:settings') && (
              <button
                onMouseDown={(e) => e.preventDefault()}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-600 focus:outline-none focus-visible:outline-none"
                title="Einstellungen"
              >
                <Settings2Icon size={20} />
              </button>
            )}
            {hasPermission('manage:help') && (
              <a href="https://intercom.help/gastroflow/de" target="_blank" rel="noopener noreferrer">
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  className="p-2 rounded-md hover:bg-gray-100 text-gray-600 focus:outline-none focus-visible:outline-none"
                  title="Hilfe"
                >
                  <HelpCircleIcon size={20} />
                </button>
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* --------------------------------- Desktop ------------------------------ */
  return (
    <div
      className="fixed top-0 left-0 h-screen bg-white text-gray-900 flex flex-col border-r border-gray-200 z-40"
      style={{ width: sidebarWidth, transition: 'width 0.2s ease' }}
    >
      {styleTag}

      {/* Header */}
      <div className="flex items-center justify-between py-6 px-4">
        <div className="flex-grow flex justify-center">
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleLogoClick}
            className="focus:outline-none focus-visible:outline-none"
            aria-label="Zum Dashboard"
            title="Dashboard"
          >
            <img
              src={isOpen ? LogoImage : COLLAPSED_LOGO_URL}
              alt="Logo"
              className={(isOpen ? 'h-5 w-auto' : 'h-8 w-8') + ' cursor-pointer'}
            />
          </button>
        </div>
      </div>

      {/* Navigation – mit unsichtbarer Scrollbar + Fades + Thread-Linien */}
      <ScrollFade className="flex-grow">
        <nav className="p-3">
          {menuItems.map((section) => {
            const hasAccessibleItems = section.items.some(item => hasPermission(item.permission));
            const isCollapsed = collapsedSections.has(section.id);
            const isSectionActive = section.items.some(item => location.pathname === item.path);
            if (!hasAccessibleItems) return null;

            return (
              <div className="mb-4" key={section.id}>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSectionClick(section)}
                  className={`flex items-center w-full px-2 py-2 text-sm rounded-md transition-colors justify-between hover:bg-gray-50 ${
                    isSectionActive ? 'font-semibold text-black' : 'text-gray-700 hover:text-gray-900'
                  } focus:outline-none focus-visible:outline-none`}
                  title={!isOpen ? section.title : undefined}
                >
                  {isOpen ? (
                    <>
                      <div className="flex items-center">
                        <section.icon size={18} className="mr-3" />
                        <span className="truncate">{section.title}</span>
                      </div>
                      {isCollapsed ? <ChevronRightIcon size={14} /> : <ChevronDownIcon size={14} />}
                    </>
                  ) : (
                    <section.icon size={18} className="mx-auto" />
                  )}
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && !isCollapsed && (
                    <motion.ul
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mt-1 space-y-1 overflow-hidden thread-wrap"
                    >
                      {section.items.map((item) =>
                        hasPermission(item.permission) ? (
                          <li
                            key={item.id}
                            className="thread-item pl-7"
                            data-active={location.pathname === item.path}
                          >
                            <button
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleItemClick(item.path)}
                              className={`flex items-center w-full px-4 py-2 text-sm rounded-md transition-colors ${
                                location.pathname === item.path
                                  ? 'text-black bg-gray-100 font-medium'
                                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                              } focus:outline-none focus-visible:outline-none`}
                              title={item.label}
                            >
                              <span className="truncate">{item.label}</span>
                            </button>
                          </li>
                        ) : null
                      )}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>
      </ScrollFade>

      {/* Toggle */}
      <div className="px-3 pb-3">
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={toggleSidebar}
          className={`w-full flex items-center justify-center ${isOpen ? 'px-4 py-2 text-sm' : 'p-2'} rounded-md hover:bg-gray-100 text-gray-600 focus:outline-none focus-visible:outline-none`}
          aria-label={isOpen ? 'Sidebar einklappen' : 'Sidebar ausklappen'}
          title={isOpen ? 'Einklappen' : 'Ausklappen'}
        >
          {isOpen ? (
            <>
              <PanelLeftClose size={18} className="mr-2" />
              <span className="truncate">Einklappen</span>
            </>
          ) : (
            <PanelLeftOpen size={18} />
          )}
        </button>
      </div>

      {/* Footer mit Profilblock */}
      <div className="p-3 border-t border-gray-200 relative" ref={profileRef}>
        {/* Profil-Trigger */}
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setProfileOpen(prev => !prev)}
          className={`w-full flex items-center ${isOpen ? 'gap-3 p-2' : 'justify-center p-2'} rounded-md bg-gray-50 active:bg-gray-100 transition-colors focus:outline-none focus-visible:outline-none`}
          aria-expanded={profileOpen}
          aria-haspopup="true"
          title={user?.name || 'Profil'}
        >
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user?.name || 'User'}
              className={`${isOpen ? 'w-9 h-9' : 'w-8 h-8'} rounded-full ring-2 ring-white object-cover`}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = 'https://ui-avatars.com/api/?name=User&size=200';
              }}
            />
          ) : (
            <div className={`${isOpen ? 'w-9 h-9' : 'w-8 h-8'} rounded-full bg-gray-200 flex items-center justify-center`}>
              <User className="w-4 h-4 text-gray-600" />
            </div>
          )}

          {isOpen && (
            <>
              <div className="flex-1 text-left min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{user?.name || 'Benutzer'}</div>
                <div className="text-xs text-gray-500 truncate">{user?.email || ''}</div>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </>
          )}
        </button>

        {/* Menü nach rechts */}
        <AnimatePresence>
          {profileOpen && (
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              transition={{ type: 'tween', duration: 0.16 }}
              className="absolute left-full bottom-3 ml-2 w-[260px] z-50"
            >
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 flex flex-col max-h-[70vh]">
                {/* Scrollbarer Mittelteil – ohne sichtbare Scrollbar */}
                <div className="flex-1 overflow-y-auto no-scrollbar py-1">
                  {profileMenuSections.map((section, idx) => {
                    const hasAccess = section.items.some(item => hasPermission(item.permission));
                    if (!hasAccess) return null;
                    return (
                      <div key={section.title} className="px-2">
                        <div className="px-2 py-1.5">
                          <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                            {section.title}
                          </h3>
                        </div>
                        {section.items.map((item) =>
                          hasPermission(item.permission) ? (
                            <button
                              key={item.name}
                              onClick={() => {
                                navigate(item.path);
                                setProfileOpen(false);
                              }}
                              className="w-full text-left px-3 py-1.5 text-[13px] text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                            >
                              {item.name}
                            </button>
                          ) : null
                        )}
                        {idx < profileMenuSections.length - 1 && hasAccess && (
                          <div className="my-2 border-t border-gray-100" />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Fixer Footer */}
                <div className="px-2 py-2 border-t border-gray-100 bg-white">
                  <button
                    onClick={() => logout({ returnTo: window.location.origin })}
                    className="w-full text-left px-3 py-1.5 text-[13px] rounded-md text-red-600 hover:bg-red-50 transition-colors"
                  >
                    Abmelden
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Buttons unten */}
        <div className={`mt-4 ${isOpen ? 'flex flex-row justify-between' : 'flex flex-col items-center space-y-2'}`}>
          {hasPermission('manage:settings') && (
            <button
              onMouseDown={(e) => e.preventDefault()}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-600 focus:outline-none focus-visible:outline-none"
              title="Einstellungen"
            >
              <Settings2Icon size={18} />
            </button>
          )}
          {hasPermission('manage:help') && (
            <a href="https://intercom.help/gastroflow/de" target="_blank" rel="noopener noreferrer" title="Hilfe">
              <button
                onMouseDown={(e) => e.preventDefault()}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-600 focus:outline-none focus-visible:outline-none"
              >
                <HelpCircleIcon size={18} />
              </button>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
